import type { OtherWork } from '../workbench/otherWork'
/** Real viewing contexts. Names never identify a Fund, position or subchain. */
import type { RecordContents, RecordTablePayload, RtSegment } from '../recordTypes'
import type { DrawerTarget } from '../objectDrawerTypes'
import type { DocumentRef } from '../../lib/documentReader'
import type { EditResult } from '../recordEdits/contract'

export interface ViewScope {
  kind: 'all' | 'client' | 'fund' | 'subchain'
  client_id?: string | null
  fund_id?: string | null
  subchain_id?: string | null
  below_entity_id?: string | null
}
export interface ClientView {
  id: string
  display_name: string | null
  folder_reference: string
  contact_hint: string | null
  source: {
    id: string
    name: string
    kind: string
    is_active: boolean
    folder_reference: string | null
  }
  fund_folders: {
    id: string
    display_name: string | null
    folder_reference: string
    match_status: 'unique' | 'ambiguous' | 'unmatched'
    matching_registered_funds: { id: string; name: string; status: string }[]
  }[]
  document_count: number
  receipt_count: number
}
export interface QueueCounts {
  card_count: number
  proposal_count: number
  removal_request_count?: number
  requirement_count: number
  request_count: number
}
export interface FundView {
  book_discovery_complete?: boolean
  book_discovery_issues?: readonly string[]
  id: string
  name: string
  root_entity_id: string | null
  legal_form: string | null
  jurisdiction: string | null
  queues: Record<'book' | 'evidence' | 'chase', QueueCounts>
  client_hints: {
    client_id: string
    client_name: string | null
    folder_id: string
    folder_reference: string
    source_id: string
    match_status: 'unique' | 'ambiguous'
    provisional: true
  }[]
  open_subchain_count: number
  unassigned_work_count: number
}
export interface Catalogue {
  book_discovery_complete?: boolean
  book_discovery_issues?: readonly string[]
  clients: ClientView[]
  client_total: number
  client_page: number
  client_page_size: number
  client_has_more: boolean
  funds: FundView[]
  unlinked_fund_ids: string[]
  unassigned_work: {
    id: string
    kind: string
    reason: string
    label?: string
    document_ids?: string[]
  }[]
  unassigned_documents: DocumentRef[]
  revision: string
}
export interface CardIdentity {
  kind: 'subchain' | 'instrument' | 'entity'
  holder_id: string | null
  instrument_id: string | null
  issuer_id: string | null
  entity_id: string | null
}
export interface RemovalWork {
  id: string
  target: { kind: 'entity' | 'instrument' | 'position' | 'trade'; id: string }
  label: string | null
  reason: string
  requested_by: string
  requested_at: string
  requested_fund_id: string
  affected_fund_ids: string[]
  document_ids: string[]
}
export interface WorkCard {
  id: string
  label: string
  identity: CardIdentity
  fund_ids: string[]
  members: { kind: string; id: string }[]
  book_proposal_ids: string[]
  close_task_ids?: string[]
  removal_request_ids?: string[]
  evidence_requirement_ids: string[]
  chase_requirement_ids: string[]
  document_ids: string[]
  revision: string
}
/** An exact proposed Book review selection, never a structural Record card. */
export interface BookDiscoveryCard {
  id: string
  label: string
  group_id: string | null
  proposal_ids: readonly string[]
  held_item_ids?: readonly string[]
  source_question_ids?: readonly string[]
  document_ids: readonly string[]
  context_fund_id: string | null
  source_client_id: string | null
  revision: string
}
export function isBookDiscoveryCard(card: WorkCard | BookDiscoveryCard): card is BookDiscoveryCard {
  return 'group_id' in card
}
export interface ContextWork {
  funds?: readonly { id: string; name: string }[]
  book_cards?: readonly BookDiscoveryCard[]
  book_discovery_complete?: boolean
  book_discovery_issues?: readonly string[]
  removal_requests?: readonly RemovalWork[]
  cards: WorkCard[]
  queues: { name: 'book' | 'evidence' | 'chase'; card_ids: string[]; counts: QueueCounts }[]
  documents: { reference: DocumentRef }[]
  fund_graphs: {
    fund_id: string
    root_entity_id: string | null
    entity_ids: string[]
    edges: {
      position_id: string
      holder_id: string
      instrument_id: string
      issuer_id: string
      record_state: string
    }[]
  }[]
  unassigned_work: { id: string; kind: string; reason: string; document_ids: string[] }[]
  open_subchain_count: number
  open_requirement_count: number
  requirements: {
    id: string
    target: { kind: string; id: string }
    code: string
    label: string
    status: string
    queue: 'book' | 'evidence' | 'chase' | null
    task_ids: string[]
    request_ids: string[]
  }[]
  tasks: {
    id: string
    task_type: string
    target_kind: string | null
    target_id: string | null
    fund_id: string | null
    status: string
  }[]
}
export interface AdditionalObjectAccess {
  kind: 'entity' | 'instrument'
  id: string
  client_ids: string[]
  document_ids: string[]
  dossier_url: string
}
export interface AdditionalObjects {
  entities: RecordTablePayload['entities']
  instruments: (RecordTablePayload['instruments'][number] & { issuer_id?: string | null })[]
  counts: RecordTablePayload['counts']
  access: AdditionalObjectAccess[]
  coverage: 'current_confirmed_only' | 'historical_membership_unavailable'
}

/** Validate the exact returned read door; never manufacture a Fund or fallback URL. */
export function sourceDossierUrl(
  access: AdditionalObjectAccess,
  target: DrawerTarget,
  scope: ViewScope
): string | null {
  if (
    !['all', 'client'].includes(scope.kind) ||
    access.kind !== target.type ||
    access.id !== target.id
  )
    return null
  if (
    typeof access.dossier_url !== 'string' ||
    !access.dossier_url.startsWith('/api/') ||
    access.dossier_url.includes('\\')
  )
    return null
  try {
    const url = new URL(access.dossier_url, 'https://nolos.invalid')
    if (
      url.origin !== 'https://nolos.invalid' ||
      url.hash ||
      url.pathname !== `/api/objects/${target.type}/${encodeURIComponent(target.id)}/record`
    )
      return null
    const entries = [...url.searchParams.entries()]
    if (scope.kind === 'client') {
      if (
        !scope.client_id ||
        !access.client_ids.includes(scope.client_id) ||
        entries.length !== 1 ||
        entries[0][0] !== 'client_id' ||
        entries[0][1] !== scope.client_id
      )
        return null
    } else if (entries.length) return null
    return access.dossier_url
  } catch {
    return null
  }
}

export function additionalObjectAccess(
  value: RecordContext,
  target: DrawerTarget,
  asOf?: string | null
): AdditionalObjectAccess | null {
  if (asOf || value.additional_objects?.coverage !== 'current_confirmed_only') return null
  if (
    value.records.some((record) =>
      target.type === 'entity'
        ? record.record.entities.some((row) => row.id === target.id)
        : target.type === 'instrument' &&
          record.record.instruments.some((row) => row.id === target.id)
    )
  )
    return null
  const rows =
    target.type === 'entity'
      ? value.additional_objects.entities
      : target.type === 'instrument'
        ? value.additional_objects.instruments
        : []
  if (!rows.some((row) => row.id === target.id && row.state !== 'proposed')) return null
  const access = value.additional_objects.access.find(
    (row) => row.kind === target.type && row.id === target.id
  )
  return access && sourceDossierUrl(access, target, value.scope) ? access : null
}

export interface RecordContext {
  other_work?: OtherWork
  additional_objects?: AdditionalObjects
  pending_edits?: readonly EditResult[]
  pending_edits_as_of?: 'current'
  scope: ViewScope
  fund_ids: string[]
  client: ClientView | null
  client_match_is_provisional: boolean
  records: {
    fund_id: string
    record: RecordTablePayload
    matrix: Record<string, unknown>[]
    notes: Record<string, unknown>
  }[]
  work: ContextWork
  work_scope: 'selected_context' | 'descendants'
  work_as_of: 'current'
  broader_work: {
    fund_id: string
    open_subchain_count: number
    unassigned_work_count: number
  } | null
  revision: string
}

export function scopeFromParams(params: URLSearchParams): ViewScope {
  const kind = params.get('scope') ?? 'all'
  if (!['all', 'client', 'fund', 'subchain'].includes(kind)) throw new Error('Unknown Record scope')
  const scope: ViewScope = {
    kind: kind as ViewScope['kind'],
    client_id: params.get('client'),
    fund_id: params.get('fund'),
    subchain_id: params.get('subchain'),
    below_entity_id: params.get('below'),
  }
  if (scope.kind === 'all' && (scope.client_id || scope.fund_id || scope.subchain_id))
    throw new Error('All records cannot carry a narrower scope')
  if (scope.kind === 'client' && (!scope.client_id || scope.fund_id || scope.subchain_id))
    throw new Error('Choose one client context')
  if (scope.kind === 'fund' && (!scope.fund_id || scope.subchain_id))
    throw new Error('Choose one fund context')
  if (scope.kind === 'subchain' && !scope.subchain_id) throw new Error('Choose an exact subchain')
  if (scope.below_entity_id && scope.kind !== 'fund')
    throw new Error('A below view needs its fund return context')
  return scope
}
export function recordHref(
  scope: ViewScope,
  options: { asOf?: string | null; evidenced?: boolean; focus?: string | null } = {}
): string {
  const params = new URLSearchParams({ scope: scope.kind })
  if (scope.client_id) params.set('client', scope.client_id)
  if (scope.fund_id) params.set('fund', scope.fund_id)
  if (scope.subchain_id) params.set('subchain', scope.subchain_id)
  if (scope.below_entity_id) params.set('below', scope.below_entity_id)
  scopeFromParams(params)
  if (options.asOf) params.set('asof', options.asOf)
  if (options.evidenced) params.set('state', 'evidenced')
  if (options.focus && /^(entity|instrument|position|segment|trade):[^\s]+$/.test(options.focus))
    params.set('focus', options.focus)
  return '/v2/record?' + params
}
/** Work is always current; carry only the viewing context and selected queue/card. */
export function workbenchHref(
  scope: ViewScope,
  queue: 'book' | 'evidence' | 'chase' | 'other' = 'book',
  cardId?: string
): string {
  const params = new URLSearchParams(recordHref(scope).split('?')[1])
  params.set('queue', queue)
  if (cardId) params.set('card', cardId)
  return '/v2/workbench?' + params
}

export function recordQuery(
  scope: ViewScope,
  options: { asOf?: string | null; evidenced?: boolean; proposed?: boolean } = {}
): string {
  const params = new URLSearchParams({
    scope: scope.kind,
    state: options.evidenced ? 'evidenced' : 'recorded',
  })
  for (const field of ['client_id', 'fund_id', 'subchain_id', 'below_entity_id'] as const)
    if (scope[field]) params.set(field, scope[field])
  if (options.asOf) params.set('as_of', options.asOf)
  if (options.proposed) params.set('include_proposed', 'true')
  return '/api/navigation/record?' + params
}
export function cardForPosition(work: ContextWork, id: string): WorkCard | undefined {
  return work.cards.find((card) =>
    card.members.some((member) => member.kind === 'position' && member.id === id)
  )
}
export function subchainScope(card: WorkCard, context: ViewScope): ViewScope {
  if (context.fund_id && !card.fund_ids.includes(context.fund_id))
    throw new Error('This subchain is not in the selected fund')
  return {
    kind: 'subchain',
    subchain_id: card.id,
    client_id: context.client_id,
    fund_id: context.fund_id,
  }
}

/** Exact H-I-I overview route; the parent fund/client stays in the URL. */
export function subchainOverviewHref(card: WorkCard, context: ViewScope): string {
  const scoped = subchainScope(card, context)
  const params = new URLSearchParams({ scope: 'subchain', subchain: card.id })
  if (scoped.fund_id) params.set('fund', scoped.fund_id)
  if (scoped.client_id) params.set('client', scoped.client_id)
  return '/v2/subchain/' + encodeURIComponent(card.id) + '?' + params.toString()
}
export interface ContextRow {
  segment: RtSegment
  fund_ids: string[]
  card_id: string | null
}
/** One physical position package, all related fund contexts. Never sum quantities. */
export function contextRows(value: RecordContext): ContextRow[] {
  const rows = new Map<string, ContextRow>()
  for (const table of value.records)
    for (const segment of table.record.segments) {
      const card = cardForPosition(value.work, segment.position_id)
      const key = segment.position_id
      const previous = rows.get(key)
      if (previous) {
        if (!previous.fund_ids.includes(table.fund_id)) previous.fund_ids.push(table.fund_id)
        if (previous.segment.group === 'inbound' && segment.group === 'chain')
          previous.segment = segment
        continue
      }
      rows.set(key, { segment, fund_ids: [table.fund_id], card_id: card?.id ?? null })
    }
  return [...rows.values()]
}

/** No-demand confirmed records stand; a missing count is not a proven zero. */
export function recordMatchesEvidence(row: { state: string; ev_total?: number }): boolean {
  return row.state === 'evidenced' || (row.state === 'recorded' && row.ev_total === 0)
}

/** Display filtering over one admitted snapshot, without changing any financial value. */
export function evidencedRecordContext(value: RecordContext): RecordContext {
  const records = value.records.map((item) => ({
    ...item,
    record: {
      ...item.record,
      state_filter: 'evidenced',
      segments: item.record.segments.filter(recordMatchesEvidence),
      entities: item.record.entities.filter(recordMatchesEvidence),
      instruments: item.record.instruments.filter(recordMatchesEvidence),
      positions: item.record.positions.filter(recordMatchesEvidence),
      trades: item.record.trades.filter(recordMatchesEvidence),
    },
  }))
  const visible = new Set(
    records.flatMap((item) => [
      ...item.record.entities.map((row) => `entity:${row.id}`),
      ...item.record.instruments.map((row) => `instrument:${row.id}`),
      ...item.record.positions.map((row) => `position:${row.id}`),
      ...item.record.trades.map((row) => `trade:${row.id}`),
    ])
  )
  const pending_edits = value.pending_edits?.filter((edit) =>
    visible.has(`${edit.target_type}:${edit.target_id}`)
  )
  const extra = value.additional_objects
  if (!extra) return { ...value, records, pending_edits }
  const entities = extra.entities.filter(recordMatchesEvidence)
  const instruments = extra.instruments.filter(recordMatchesEvidence)
  return {
    ...value,
    records,
    pending_edits,
    additional_objects: {
      ...extra,
      entities,
      instruments,
      access: extra.access.filter((access) =>
        (access.kind === 'entity' ? entities : instruments).some((row) => row.id === access.id)
      ),
    },
  }
}

/** Unique stored rows; no aggregate Fund identity and no summed shared balance. */
export function recordContents(
  value: RecordContext,
  fullCounts?: RecordContents['counts']
): RecordContents {
  const unique = <T extends { id: string }>(rows: T[]) => [
    ...new Map(rows.map((row) => [row.id, row])).values(),
  ]
  const tables = value.records.map((row) => row.record)
  const extra =
    value.additional_objects?.coverage === 'current_confirmed_only' &&
    ['all', 'client'].includes(value.scope.kind)
      ? value.additional_objects
      : null
  // Existing Fund rows win any duplicate ID. Additional objects never create
  // positions, segments, trades or a synthetic Fund identity.
  const entities = unique([
    ...(extra?.entities ?? []).filter((row) => row.state !== 'proposed'),
    ...tables.flatMap((table) => table.entities),
  ])
  const instruments = unique([
    ...(extra?.instruments ?? []).filter((row) => row.state !== 'proposed'),
    ...tables.flatMap((table) => table.instruments),
  ])
  const positions = unique(tables.flatMap((table) => table.positions))
  const trades = unique(tables.flatMap((table) => table.trades))
  const counts = { recorded: 0, evidenced: 0, proposed: 0 }
  for (const object of [...entities, ...instruments, ...positions, ...trades]) {
    if (object.state === 'proposed') counts.proposed++
    else counts.recorded++
    if (recordMatchesEvidence(object)) counts.evidenced++
  }
  return {
    segments: contextRows(value).map((row) => row.segment),
    entities,
    instruments,
    positions,
    trades,
    counts: fullCounts ?? counts,
  }
}

export function recordCsv(segments: RtSegment[]): string {
  const cell = (value: unknown) => {
    let text = value === null || value === undefined ? '' : String(value)
    if (typeof value === 'string' && /^[=+@\-\t\r]/.test(text)) text = "'" + text
    return '"' + text.replace(/"/g, '""') + '"'
  }
  const header = [
    'Holder',
    'Instrument',
    'Issuer',
    'Position ID',
    'Balance',
    'Currency',
    'Commitment',
    'Drawn',
    'Undrawn',
    'As of',
  ]
  return [
    header,
    ...segments.map((s) => [
      s.holder_name,
      s.instrument_name,
      s.issuer_name,
      s.position_id,
      s.commitment_based ? null : s.units_held,
      s.units_currency,
      s.committed_amount,
      s.drawn_amount,
      s.undrawn_amount,
      s.as_of,
    ]),
  ]
    .map((row) => row.map(cell).join(','))
    .join('\r\n')
}

export type RecordTarget = { type: 'entity' | 'instrument' | 'position' | 'trade'; id: string }
/** A document return or bookmark can select only an object in this exact read. */
export function recordTarget(raw: string | null, table: RecordContents): RecordTarget | null {
  const match = /^(entity|instrument|position|segment|trade):([^\s]+)$/.exec(raw ?? '')
  if (!match) return null
  const type = match[1] === 'segment' ? 'position' : (match[1] as RecordTarget['type'])
  const rows =
    type === 'entity'
      ? table.entities
      : type === 'instrument'
        ? table.instruments
        : type === 'position'
          ? table.positions
          : table.trades
  return rows.some((row) => row.id === match[2]) ? { type, id: match[2] } : null
}
